/* eslint-disable max-lines-per-function */
import {
  Button,
  SheetBody,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  TextArea,
} from '@e2n/ui/v2';
import { useRotationsplanById } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanById/useRotationsplanById';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { UPDATE_BEWERTUNG_FORM_ID } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/constants';
import { useForm } from '@/domain/mitarbeiter/features/rotationsplan/features/UpdateBewertung/useForm';
import { useRegisterSheetForm } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/SheetWrapper/useRegisterSheetForm';
import { FieldBaseV2 } from '@/components/form';
import { ArbeitsbereichStationInfo } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateBewertung/components/ArbeitsbereichStationInfo';
import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { Rating } from '@/components/FeadbackToggle/Rating';
import { DeleteBewertung } from '@/domain/mitarbeiter/features/rotationsplan/features/DeleteBewertung/DeleteBewertung';

export function UpdateBewertung() {
  const { t } = useTranslation();
  const { id, stationId, bewertungId } = useParams();
  const { rotationsplan } = useRotationsplanById(id ?? '');

  if (!rotationsplan || !stationId || !bewertungId || !id) {
    return <Navigate to="/mitarbeiter/rotationsplaene" />;
  }

  const station = rotationsplan.allStationen
    .filter((station) => station instanceof ArbeitsbereichStation)
    .find((station) => station.id === stationId);

  if (!station || !station.bewertung) {
    return <Navigate to={`/mitarbeiter/rotationsplan/${rotationsplan.id}`} />;
  }

  const ratings = [
    {
      value: 1,
    },
    {
      value: 2,
    },
    {
      value: 3,
    },
    {
      value: 4,
    },
    {
      value: 5,
    },
  ];

  const form = useForm({
    bewertung: station.bewertung,
    rotationsplanId: id,
    stationId,
  });

  useRegisterSheetForm(form);

  return (
    <div className="flex h-full flex-col bg-backgrounds-secondary">
      <SheetHeader
        actions={
          <>
            <DeleteBewertung bewertung={station.bewertung} />
            <Button form={UPDATE_BEWERTUNG_FORM_ID}>
              {t('actions.fertig')}
            </Button>
          </>
        }>
        <SheetTitle className="flex-1">
          {t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.updateBewertung.title',
          )}
        </SheetTitle>
      </SheetHeader>
      <SheetBody className="flex flex-col gap-6 md:px-6 grow overflow-auto">
        <ArbeitsbereichStationInfo station={station} />
        <form
          id={UPDATE_BEWERTUNG_FORM_ID}
          className="flex flex-col gap-6 h-full"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}>
          <form.AppField name="sterne">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <FieldBaseV2
                  data-invalid={isInvalid}
                  label={t(
                    'domain.mitarbeiter.rotationsplan.rotationsplaene.createBewertung.fields.sterne.label',
                  )}>
                  <Rating
                    ratings={ratings}
                    defaultValue={field.state.value}
                    onChange={(e) => field.handleChange(e)}
                    allowDeselect
                    starSize={36}
                    filledColor="var(--color-brand-red-200)"
                    emptyColor="var(--color-neutrals-v2-500)"
                  />
                </FieldBaseV2>
              );
            }}
          </form.AppField>
          <form.AppField name="beurteilung">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <FieldBaseV2
                  data-invalid={isInvalid}
                  label={t(
                    'domain.mitarbeiter.rotationsplan.rotationsplaene.createBewertung.fields.beurteilung.label',
                  )}>
                  <TextArea
                    id={field.name}
                    placeholder={t(
                      'domain.mitarbeiter.rotationsplan.rotationsplaene.createBewertung.fields.beurteilung.placeholder',
                    )}
                    defaultValue={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    maxLength={400}
                    showCount
                  />
                </FieldBaseV2>
              );
            }}
          </form.AppField>
        </form>
      </SheetBody>
      <SheetFooter>
        <DeleteBewertung bewertung={station.bewertung} />
        <Button
          type="submit"
          disabled={!form.state.isValid || form.state.isSubmitting}
          form={UPDATE_BEWERTUNG_FORM_ID}>
          {t('actions.fertig')}
        </Button>
      </SheetFooter>
    </div>
  );
}
